import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import {
  useDeleteIngestIntakeMutation,
  type IngestIntake
} from '@axilon-apps/domains-ontology-ui-state'

import { readMutationError } from '../../../components/explorer'
import { Button } from '../../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog'
import { intakeMergeReviewPath } from './intake-view'

interface DeleteIntakeDialogProps {
  intake: IngestIntake | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteIntakeDialog({ intake, open, onOpenChange }: DeleteIntakeDialogProps) {
  const [deleteIntake, deleteState] = useDeleteIngestIntakeMutation()
  const [pending, setPending] = React.useState<number | null>(null)
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    if (open) {
      setPending(null)
      setError('')
    }
  }, [open, intake?.id])

  if (!intake) return null

  const submit = async (force: boolean) => {
    setError('')
    try {
      await deleteIntake({ id: intake.id, force }).unwrap()
      toast.success(`Deleted ${intake.name}`)
      onOpenChange(false)
    } catch (deleteError) {
      const pendingCount = pendingFromError(deleteError)
      if (!force && pendingCount !== null) {
        setPending(pendingCount)
        return
      }
      setError(readMutationError(deleteError))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !deleteState.isLoading && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete {intake.name}?</DialogTitle>
          <DialogDescription>
            Its received-file records are removed. Jobs already run stay in Runs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm text-ink-2">
          {pending !== null ? (
            <p className="font-medium text-st-down">
              {pending} {pending === 1 ? 'file is' : 'files are'} waiting to run and will be
              discarded — delete anyway?
            </p>
          ) : null}
          {intake.branch ? (
            <p>
              Branch <span className="font-mono text-ink">{intake.branch.name}</span> is not touched
              — merge or delete it from Branches.{' '}
              <Link
                to={intakeMergeReviewPath(intake.branch.id, intake.id)}
                className="text-st-info hover:underline"
              >
                Review &amp; merge
              </Link>
            </p>
          ) : null}
          {error ? (
            <p role="alert" className="text-st-down">
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={deleteState.isLoading}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={deleteState.isLoading}
            onClick={() => void submit(pending !== null)}
          >
            {deleteState.isLoading ? <Loader2 className="size-4 animate-spin" /> : null}
            {pending !== null ? 'Delete anyway' : 'Delete intake'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function pendingFromError(error: unknown): number | null {
  if (!error || typeof error !== 'object') return null
  const payload = error as { status?: unknown; data?: unknown }
  if (payload.status !== 409 || !payload.data || typeof payload.data !== 'object') return null
  const data = payload.data as {
    reason?: unknown
    pending?: unknown
    details?: { pending?: unknown }
  }
  if (data.reason !== 'INTAKE_HAS_PENDING_ITEMS') return null
  const value = data.pending ?? data.details?.pending
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}
